//import 'ol/ol.css';
import { Map, View, Feature } from 'ol';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { LineString } from 'ol/geom';
import { Style, Stroke, Fill } from 'ol/style';
import { Draw as DrawInteraction } from 'ol/interaction';
import Snap from 'ol/interaction/Snap';
import GeometryType from 'ol/geom/GeometryType';
import { MapStyles } from './MapStyles';
import { OptionalTracingLayer } from './MainMap';
import { CAMSVectorSource } from '../../Extensions/CAMSOLExtensions';
import { RouteMapStyles } from './MapStyleConfig/RouteMapStyles';
import { LandMapStyles } from './MapStyleConfig/LandMapStyles';

export class MapTrace {

    // *****************************************************
    // Global variables
    private enabled = false;
    private map;
    private drawInteraction;
    private modifyInteraction;
    private snapInteraction;
    private tracingFeature;
    private startPoint;
    private endPoint;
    private drawing = false;
    private tracingLayerType: OptionalTracingLayer;
    private mapStyles: MapStyles;

    // This is the vector that is being snapped too
    private baseVector; 

    // Preview line
    // this line only appears when we're tracing a feature outer ring
    private previewLine;
    private previewVector;
    private getFeatureOptions;

    // *****************************************************
    // Constructor
    public constructor(map, mapStyles: MapStyles ) {
        const self = this;
        self.map = map;
        self.mapStyles = mapStyles;

        // Add map events
        self.addMapClickEventListeners();
        self.addMapPointerMoveEventListeners();
    }

    // *****************************************************
    // Public functions
    public startTracingNewFeature(baseVector, drawInteraction, tracingLayerType : OptionalTracingLayer) {
        const self = this;

        self.enabled = true;
        self.baseVector = baseVector;
        self.drawInteraction = drawInteraction;
        self.tracingLayerType = tracingLayerType;

        // Create snap interaction
        self.snapInteraction = new Snap({
            pixelTolerance: 3, // Default=10
            source: baseVector.getSource(),
        });

        // Create preview vector layer
        self.previewLine = new Feature({
            geometry: new LineString([]),
        });

        self.previewVector = new VectorLayer({
            source: new VectorSource({
                features: [self.previewLine],
            }),
            style: new Style({
                stroke: new Stroke({
                    color: 'rgba(255, 0, 0, 1)',
                    width: 2,
                }),
            }),
        });
        self.map.addLayer(self.previewVector);

        // Update style of the baseVector to add in points
        switch (self.tracingLayerType) {
            case OptionalTracingLayer.routeLineLayer:
                self.mapStyles.RouteMapStyles.enabledRouteTracingNodes = true;
                break;
            case OptionalTracingLayer.landPolygonLayer:
                self.mapStyles.LandMapStyles.enabledLandTracingNodes = true;
                break;
        }
        
        (self.baseVector.getSource() as CAMSVectorSource).forceRefresh();

        // Create options
        self.getFeatureOptions = {
            hitTolerance: 3,
            layerFilter: function (layer) {
                return layer === baseVector;
            }
        };

        // Add the map interactions
        self.addDrawInteraction();
    }

    public startTracingModifyFeature(baseVector, modifyInteraction) {

        // TODO
        return;
        const self = this;

        self.enabled = true;
        self.baseVector = baseVector;
        self.modifyInteraction = modifyInteraction;

        // Create snap interaction
        self.snapInteraction = new Snap({
            source: baseVector.getSource(),
        });

        // Create preview vector layer
        self.previewLine = new Feature({
            geometry: new LineString([]),
        });

        self.previewVector = new VectorLayer({
            source: new VectorSource({
                features: [self.previewLine],
            }),
            style: new Style({
                stroke: new Stroke({
                    color: 'rgba(255, 0, 0, 1)',
                    width: 2,
                }),
            }),
        });
        self.map.addLayer(self.previewVector);

        // Update style of the baseVector to add in points
        switch (self.tracingLayerType) {
            case OptionalTracingLayer.routeLineLayer:
                self.mapStyles.RouteMapStyles.enabledRouteTracingNodes = true;
                break;
            case OptionalTracingLayer.landPolygonLayer:
                self.mapStyles.LandMapStyles.enabledLandTracingNodes = true;
                break;
        }
        self.baseVector.getSource().refresh();

        // Create options
        self.getFeatureOptions = {
            hitTolerance: 10,
            layerFilter: function (layer) {
                return layer === baseVector;
            }
        };

        // Add the map interactions
        self.addModifyInteraction();
    }

    public stopTracing(): void {
        const self = this;
        self.enabled = false;
        switch (self.tracingLayerType) {
            case OptionalTracingLayer.routeLineLayer:
                self.mapStyles.RouteMapStyles.enabledRouteTracingNodes = false;
                break;
            case OptionalTracingLayer.landPolygonLayer:
                self.mapStyles.LandMapStyles.enabledLandTracingNodes = false;
                break;
        }
        
        if (self.map != null) {
            if (self.previewVector != null) self.map.removeLayer(self.previewVector);
            if (self.snapInteraction != null) self.map.removeInteraction(self.snapInteraction);
            if (self.drawInteraction != null) self.map.removeInteraction(self.drawInteraction);
            if (self.modifyInteraction != null) self.map.removeInteraction(self.modifyInteraction);
            if (self.baseVector != null && (self.drawInteraction !== null || self.modifyInteraction != null)) (self.baseVector.getSource() as CAMSVectorSource).forceRefresh();
        }

        self.drawInteraction = null;
        self.modifyInteraction = null;
        self.snapInteraction = null;
    }

    // *****************************************************
    // Map interactions
    private addDrawInteraction(): void {
        const self = this;

        self.drawInteraction.on('drawstart', function () {
            self.drawing = true;
        });
        self.drawInteraction.on('drawend', function () {
            self.drawing = false;
            self.previewLine.getGeometry().setCoordinates([]);
            self.tracingFeature = null;
        });

        self.map.addInteraction(self.drawInteraction);
        self.map.addInteraction(self.snapInteraction);
    }

    private addModifyInteraction(): void {
        const self = this;

        self.modifyInteraction.on('modifystart', function () {
            self.drawing = true;
        });
        self.modifyInteraction.on('modifyend', function () {
            self.drawing = false;
            self.previewLine.getGeometry().setCoordinates([]);
            self.tracingFeature = null;
        });

        self.map.addInteraction(self.modifyInteraction);
        self.map.addInteraction(self.snapInteraction);
    }

    // *****************************************************
    // Map actions
    private addMapClickEventListeners(): void {
        const self = this;

        // the click event is used to start/end tracing around a feature
        self.map.on('click', function (event) {
            if (self.enabled == false) {
                return;
            }

            if (!self.drawing) {
                return;
            }

            if (self.drawInteraction != null) self.mapclick_newFeature(event);
            if (self.modifyInteraction != null) self.mapclick_modifyFeature(event);
        });

    }

    private addMapPointerMoveEventListeners(): void {
        const self = this;

        // the pointermove event is used to show a preview of the result of the tracing
        self.map.on('pointermove', function (event) {
            if (self.enabled == false) {
                return;
            }

            if (self.drawInteraction != null) self.pointermove_newFeature(event);
            if (self.modifyInteraction != null) self.pointermove_modifyFeature(event);
        });
    }

    // *****************************************************
    // New features map events
    private mapclick_newFeature(event): void {
        const self = this;

        var hit = false;
        self.map.forEachFeatureAtPixel(
            event.pixel,
            function (feature) {
                if (self.tracingFeature && self.isSameFeature(self.tracingFeature, feature) == false) {
                    return;
                }

                hit = true;
                var coord = self.map.getCoordinateFromPixel(event.pixel);

                // second click on the tracing feature: append the ring coordinates
                if (self.isSameFeature(self.tracingFeature, feature) == true) {
                    self.endPoint = self.tracingFeature.getGeometry().getClosestPoint(coord);
                    var appendCoords = self.getPartialRingCoords(
                        self.tracingFeature,
                        self.startPoint,
                        self.endPoint
                    );
                    self.drawInteraction.removeLastPoint();
                    self.drawInteraction.appendCoordinates(appendCoords);
                    self.tracingFeature = null;
                }

                // start tracing on the feature ring
                self.tracingFeature = feature;
                self.startPoint = self.tracingFeature.getGeometry().getClosestPoint(coord);
            },
            self.getFeatureOptions
        );

        if (!hit) {
            // clear current tracing feature & preview
            self.previewLine.getGeometry().setCoordinates([]);
            self.tracingFeature = null;
        }
    }

    private pointermove_newFeature(event): void {
        const self = this;

        if (self.tracingFeature && self.drawing) {
            var coord = null;
            self.map.forEachFeatureAtPixel(
                event.pixel,
                function (feature) {
                    if (self.isSameFeature(self.tracingFeature, feature) == true) {
                        coord = self.map.getCoordinateFromPixel(event.pixel);
                    }
                },
                self.getFeatureOptions
            );

            var previewCoords = [];
            if (coord) {
                self.endPoint = self.tracingFeature.getGeometry().getClosestPoint(coord);
                previewCoords = self.getPartialRingCoords(
                    self.tracingFeature,
                    self.startPoint,
                    self.endPoint
                );
            }
            self.previewLine.getGeometry().setCoordinates(previewCoords);
        }
  
    }

    // *****************************************************
    // Modify features map events
    private mapclick_modifyFeature(event): void {
        const self = this;

        // todo
    }

    private pointermove_modifyFeature(event): void {
        const self = this;
        // todo
    }

    // *****************************************************
    // Misc
    private isSameFeature(f1, f2): boolean {
        if (f1 != null && f2 != null) {
            var p1 = f1.getProperties(); var p2 = f2.getProperties();
            if (p1.MaintCode!= null && p1.MaintCode === p2.MaintCode) return true;
            if (p1.InfraCode != null && p1.InfraCode === p2.InfraCode) return true;
            if (p1.RouteCode != null && p1.RouteCode === p2.RouteCode) return true;
            if (p1.LandCode != null && p1.LandCode === p2.LandCode) return true;
        }
        return false;
    }

    // *****************************************************
    // Math utilities
    // coordinates; will return the length of the [a, b] segment
    private length(a, b): number {
        return Math.sqrt(
            (b[0] - a[0]) * (b[0] - a[0]) + (b[1] - a[1]) * (b[1] - a[1])
        );
    }

    // coordinates; will return true if c is on the [a, b] segment
    private isOnSegment(c, a, b): boolean {
        const self = this;

        var lengthAc = self.length(a, c);
        var lengthAb = self.length(a, b);
        var dot =
            ((c[0] - a[0]) * (b[0] - a[0]) + (c[1] - a[1]) * (b[1] - a[1])) / lengthAb;
        return Math.abs(lengthAc - dot) < 1e-6 && lengthAc < lengthAb;
    }

    // modulo for negative values, eg: mod(-1, 4) returns 3
    private mod(a, b): number {
        return ((a % b) + b) % b;
    }

    // returns a coordinates array which contains the segments of the feature's
    // outer ring between the start and end points
    // Note: this assumes the base feature is a single polygon
    private getPartialRingCoords(feature, startPoint, endPoint): any[] {
        const self = this;

        var ringCoords;
        if (feature.getGeometry().getType() == GeometryType.LINE_STRING) {
            var line = feature.getGeometry();
            ringCoords = line.getCoordinates();
        }
        else if (feature.getGeometry().getType() == GeometryType.POLYGON) {
            var polygon = feature.getGeometry();
            if (polygon.getType() === 'MultiPolygon') {
                polygon = polygon.getPolygon(0);
            }
            ringCoords = polygon.getLinearRing().getCoordinates();
        }

        var i, pointA, pointB, startSegmentIndex = -1;
        for (i = 0; i < ringCoords.length; i++) {
            pointA = ringCoords[i];
            pointB = ringCoords[self.mod(i + 1, ringCoords.length)];

            // check if this is the start segment dot product
            if (self.isOnSegment(startPoint, pointA, pointB)) {
                startSegmentIndex = i;
                break;
            }
        }

        var cwCoordinates = [];
        var cwLength = 0;
        var ccwCoordinates = [];
        var ccwLength = 0;

        // build clockwise coordinates
        for (i = 0; i < ringCoords.length; i++) {
            pointA =
                i === 0
                    ? startPoint
                    : ringCoords[self.mod(i + startSegmentIndex, ringCoords.length)];
            pointB = ringCoords[self.mod(i + startSegmentIndex + 1, ringCoords.length)];
            cwCoordinates.push(pointA);

            if (self.isOnSegment(endPoint, pointA, pointB)) {
                cwCoordinates.push(endPoint);
                cwLength += self.length(pointA, endPoint);
                break;
            } else {
                cwLength += self.length(pointA, pointB);
            }
        }

        // build counter-clockwise coordinates
        for (i = 0; i < ringCoords.length; i++) {
            pointA = ringCoords[self.mod(startSegmentIndex - i, ringCoords.length)];
            pointB =
                i === 0
                    ? startPoint
                    : ringCoords[self.mod(startSegmentIndex - i + 1, ringCoords.length)];
            ccwCoordinates.push(pointB);

            if (self.isOnSegment(endPoint, pointA, pointB)) {
                ccwCoordinates.push(endPoint);
                ccwLength += self.length(endPoint, pointB);
                break;
            } else {
                ccwLength += self.length(pointA, pointB);
            }
        }

        // keep the shortest path
        return ccwLength < cwLength ? ccwCoordinates : cwCoordinates;
    }
}




